import { enableProdMode, importProvidersFrom, inject, provideZoneChangeDetection } from '@angular/core';
import { tap, finalize } from 'rxjs/operators';


import { environment } from './app/environments/environment';
import { bootstrapApplication } from "@angular/platform-browser";
import { AppComponent } from "./app/app.component";
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from "@angular/material/form-field";
import { routes } from "./app/sub-modules/app-routing.module";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipDefaultOptions } from "@angular/material/tooltip";
import { FullscreenOverlayContainer, OverlayContainer } from "@angular/cdk/overlay";
import { provideKeycloakAngular } from "./app/factories/keycloak-init.factory";
import { ErrorStateMatcher, ShowOnDirtyErrorStateMatcher } from "@angular/material/core";
import { HTTP_INTERCEPTORS, HttpInterceptorFn, provideHttpClient, withInterceptors, withInterceptorsFromDi } from "@angular/common/http";
import { includeBearerTokenInterceptor } from "keycloak-angular";
import { ActiveAccountInterceptor } from "./app/interceptor/active-account.interceptor";
import { provideRouter } from "@angular/router";
import { SecurePipe } from "./app/shared/pipes";
import { MatomoModule, MatomoRouterModule } from "ngx-matomo-client";
import { SocketIoConfig, SocketIoModule } from "ngx-socket-io";
import * as PlotlyJS from 'plotly.js-dist-min';
import { PlotlyModule } from 'angular-plotly.js';
import { LoadingBarModule, LoadingBarService } from "@ngx-loading-bar/core";
import { NGX_LOADING_BAR_IGNORED } from "@ngx-loading-bar/http-client";

PlotlyModule.plotlyjs = PlotlyJS;

// Workaround for angular-gridster2 v21 NG0950 crash on hard refresh.
// Gridster's options input is a required signal; during view teardown triggered
// by auth-state @if switching, ngOnDestroy fires before the signal is set.
import { Gridster } from 'angular-gridster2';
const _origGridsterDestroy = Gridster.prototype.ngOnDestroy;
Gridster.prototype.ngOnDestroy = function (this: any) {
    try {
        _origGridsterDestroy.call(this);
    } catch (e) {
        // NG0950: required input not yet available — safe to swallow during teardown
    }
};

export const WireOptions: MatTooltipDefaultOptions = {
    showDelay: 0,
    hideDelay: 0,
    touchGestures: 'auto',
    position: 'below',
    touchendHideDelay: 0,
    disableTooltipInteractivity: true,
};

if (environment.production) {
    enableProdMode();
}
const analytic_domain = 'https://analytics.example.com/matomo.js';
const analytic_sites = {
    'http://localhost:4200': 1,
    'https://production.url': 2
};

const matomoModule = MatomoModule.forRoot({
    scriptUrl: analytic_domain,
    trackers: [
        {
            trackerUrl: window.location.origin,
            siteId: analytic_sites[window.location.origin] || 0
        }
    ]
});

const socketIoConfig: SocketIoConfig = {
    url: window.location.origin,
    options: {
        timeout: 20000,
        reconnection: true,
        reconnectionDelay: 1000,
        randomizationFactor: 0.5,
        reconnectionDelayMax: 10000,
        transports: ['websocket', 'polling']
    }
};

/**
 * Functional interceptor replacement for LoadingBarHttpClientModule.
 * LoadingBarHttpClientModule can't be used with provideHttpClient() because it
 * internally imports HttpClientModule, which conflicts with provideHttpClient().
 */
const loadingBarInterceptor: HttpInterceptorFn = (req, next) => {
    if (req.context.get(NGX_LOADING_BAR_IGNORED)) {
        return next(req);
    }
    const loader = inject(LoadingBarService);
    const ref = loader.useRef('http');
    let started = false;
    return next(req).pipe(
        tap(() => {
            if (!started) {
                ref.start();
                started = true;
            }
        }),
        finalize(() => started && ref.complete())
    );
};

// Use this once all COMPONENTS, DIRECTIVES and PIPES are standalone
bootstrapApplication(AppComponent, {
    providers: [
        provideRouter(routes),
        importProvidersFrom(BrowserAnimationsModule),
        importProvidersFrom(matomoModule),
        importProvidersFrom(MatomoRouterModule),
        importProvidersFrom(SocketIoModule.forRoot(socketIoConfig)),
        importProvidersFrom(LoadingBarModule),
        { provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: WireOptions },
        { provide: 'Window', useValue: window },
        [{ provide: OverlayContainer, useClass: FullscreenOverlayContainer }],
        provideKeycloakAngular(),
        provideZoneChangeDetection({ eventCoalescing: true }),
        { provide: ErrorStateMatcher, useClass: ShowOnDirtyErrorStateMatcher },
        { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { subscriptSizing: 'dynamic' } },
        provideHttpClient(withInterceptors([includeBearerTokenInterceptor, loadingBarInterceptor]), withInterceptorsFromDi()),
        { provide: HTTP_INTERCEPTORS, useClass: ActiveAccountInterceptor, multi: true },
        { provide: SecurePipe, useClass: SecurePipe }
    ]
}).then(ref => {
}).catch(err => console.error(err));
